// Task #1660 — wrapper apiClient per tutte le rotte messenger v2.
// Le rotte nuove (`/messenger/link-preview`, `/messenger/me/presence`,
// `/messenger/me/dnd`, `/messenger/search/quick`, `/messenger/messages/:id/history`)
// sono whitelisted in `TENANT_AGNOSTIC_PREFIXES` (api.ts) e arrivano sotto
// `protectedRouter` (tenant da JWT). Le rotte storiche (conversations,
// messages, reactions, …) restano in dual-mount: apiClient prefisserà il
// tenantSlug per back-compat finché non chiuso il task di migrazione.
//
// Tutte le chiamate passano per `apiClient` — niente `fetch()` diretto.

import { apiClient, type ApiResponse } from '@/lib/api';
import type {
  ChatMessage, ConversationSummary, LinkPreviewCard,
  ParticipantBrief, PresenceState, PresenceStatus, QuickSwitchResult,
} from '../types';

export interface MessengerEditHistoryEntry {
  id: string;
  prev_content: string | null;
  edited_by: string | null;
  editor_first_name: string | null;
  editor_last_name: string | null;
  edited_at: string;
}

export interface MessengerEditHistory {
  current_content: string | null;
  edited_at: string | null;
  history: MessengerEditHistoryEntry[];
}

export const messengerApi = {
  listConversations(params: { type?: string; search?: string } = {}): Promise<ApiResponse<ConversationSummary[]>> {
    const q = new URLSearchParams();
    if (params.type && params.type !== 'all' && params.type !== 'unread') q.set('type', params.type);
    if (params.search) q.set('search', params.search);
    const qs = q.toString();
    return apiClient.get<ConversationSummary[]>(`/messenger/conversations${qs ? `?${qs}` : ''}`);
  },

  getMessages(convId: string, cursor?: string, limit = 50): Promise<ApiResponse<ChatMessage[]>> {
    const q = new URLSearchParams({ limit: String(limit) });
    if (cursor) q.set('cursor', cursor);
    return apiClient.get<ChatMessage[]>(`/messenger/conversations/${convId}/messages?${q.toString()}`);
  },

  sendMessage(convId: string, body: { content?: string; reply_to_id?: string }): Promise<ApiResponse<ChatMessage>> {
    return apiClient.post<ChatMessage>(`/messenger/conversations/${convId}/messages`, body);
  },

  sendFiles(convId: string, files: File[], content?: string): Promise<ApiResponse<ChatMessage>> {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    if (content) fd.append('content', content);
    return apiClient.post<ChatMessage>(`/messenger/conversations/${convId}/messages`, fd);
  },

  sendVoice(convId: string, blob: Blob, durationMs: number): Promise<ApiResponse<ChatMessage>> {
    const fd = new FormData();
    const ext = blob.type.includes('mp4') ? 'm4a' : 'webm';
    fd.append('files', new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type || 'audio/webm' }));
    fd.append('message_type', 'voice');
    fd.append('voice_duration_ms', String(Math.round(durationMs)));
    return apiClient.post<ChatMessage>(`/messenger/conversations/${convId}/messages`, fd);
  },

  editMessage(messageId: string, content: string): Promise<ApiResponse<ChatMessage>> {
    return apiClient.patch<ChatMessage>(`/messenger/messages/${messageId}`, { content });
  },

  deleteMessage(messageId: string): Promise<ApiResponse<unknown>> {
    return apiClient.delete(`/messenger/messages/${messageId}`);
  },

  addReaction(messageId: string, emoji: string): Promise<ApiResponse<unknown>> {
    return apiClient.post(`/messenger/messages/${messageId}/reactions`, { emoji });
  },

  removeReaction(messageId: string, emoji: string): Promise<ApiResponse<unknown>> {
    return apiClient.delete(`/messenger/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`);
  },

  markRead(convId: string): Promise<ApiResponse<unknown>> {
    return apiClient.post(`/messenger/conversations/${convId}/read`);
  },

  togglePin(messageId: string, pinned: boolean): Promise<ApiResponse<unknown>> {
    return pinned
      ? apiClient.delete(`/messenger/messages/${messageId}/pin`)
      : apiClient.post(`/messenger/messages/${messageId}/pin`);
  },

  toggleFavorite(messageId: string, fav: boolean): Promise<ApiResponse<unknown>> {
    return fav
      ? apiClient.delete(`/messenger/messages/${messageId}/favorite`)
      : apiClient.post(`/messenger/messages/${messageId}/favorite`);
  },

  searchUsers(search: string): Promise<ApiResponse<ParticipantBrief[]>> {
    return apiClient.get<ParticipantBrief[]>(`/messenger/users${search ? `?search=${encodeURIComponent(search)}` : ''}`);
  },

  createDirect(targetUserId: string): Promise<ApiResponse<ConversationSummary>> {
    return apiClient.post<ConversationSummary>(`/messenger/conversations`, {
      type: 'direct', participants: [targetUserId],
    });
  },

  getPresence(): Promise<ApiResponse<string[]>> {
    return apiClient.get<string[]>(`/messenger/presence`);
  },

  // ─── Task #1660 — endpoint nuovi (tenant-from-JWT) ───────────────────────

  linkPreview(url: string): Promise<ApiResponse<LinkPreviewCard>> {
    return apiClient.post<LinkPreviewCard>(`/messenger/link-preview`, { url });
  },

  editHistory(messageId: string): Promise<ApiResponse<MessengerEditHistory>> {
    return apiClient.get<MessengerEditHistory>(`/messenger/messages/${messageId}/history`);
  },

  setPresenceStatus(status: PresenceStatus): Promise<ApiResponse<{ status: PresenceStatus; userId: string }>> {
    return apiClient.patch(`/messenger/me/presence`, { status });
  },

  setDnd(until: string | null, allowMentions: boolean): Promise<ApiResponse<{ dnd_until: string | null; dnd_allow_mentions: boolean }>> {
    return apiClient.patch(`/messenger/me/dnd`, { until, allow_mentions: allowMentions });
  },

  quickSearch(q: string): Promise<ApiResponse<QuickSwitchResult>> {
    return apiClient.get<QuickSwitchResult>(`/messenger/search/quick?q=${encodeURIComponent(q)}`);
  },

  // ─── Info panel: pinned / favorites / shared files (parità monolite) ────

  getPinned(convId: string): Promise<ApiResponse<ChatMessage[]>> {
    return apiClient.get<ChatMessage[]>(`/messenger/conversations/${convId}/pinned`);
  },

  getFavorites(): Promise<ApiResponse<ChatMessage[]>> {
    return apiClient.get<ChatMessage[]>(`/messenger/favorites`);
  },

  // Inoltro: backend non ha endpoint dedicato — riusiamo POST messages
  // con marker `[Inoltrato da …]` per preservare attribuzione (parità monolite).
  forwardMessage(targetConvId: string, originalSenderName: string, content: string): Promise<ApiResponse<ChatMessage>> {
    return apiClient.post<ChatMessage>(`/messenger/conversations/${targetConvId}/messages`, {
      content: `[Inoltrato da ${originalSenderName}]: ${content}`,
    });
  },

  createTaskFromMessage(messageId: string, body: {
    title: string;
    priority: string;
    due_date?: string;
    assignee_id?: string;
  }): Promise<ApiResponse<{ id: string }>> {
    return apiClient.post<{ id: string }>(`/messenger/messages/${messageId}/create-task`, body);
  },
};

// Re-export per evitare ai consumer di importare PresenceState dal type file
// quando già hanno il client.
export type { PresenceState, PresenceStatus, QuickSwitchResult };
